import React, {useImperativeHandle, useLayoutEffect, useMemo, useRef} from "react";
import {
    DomElementAnimator,
    DomElementResolver,
    useInstanceValue,
    useObservable,
    useRenderState
} from "@wix/devzai-utils-react";
import {
    assertDefined,
    assertNotNullable, Evaluable,
    evaluateFunction,
    evaluateWhenFunction,
    IArea,
    iterableMapToArray,
    ObservableSet
} from "@wix/devzai-utils-common";
import {elementGetClientOuterAreaRelativeToAnotherElement} from "@wix/devzai-utils-dom";
import ReactDOM from "react-dom";

type AnimatedListItemInfo = {
    ref: React.RefObject<DomElementResolver>;
    element: React.ReactElement
}

/**
 * Logic inspiration:
 * https://codesandbox.io/s/reorder-elements-with-slide-transition-and-react-hooks-flip-211f2?file=/src/AnimateBubbles.js
 */

export const ListItemsAnimator = React.memo(React.forwardRef(function ListItemsAnimator (props: ListItemsAnimator.Props, ref: React.Ref<ListItemsAnimator>) {
    const {
        children,
        appearTransition,
        disappearTransition,
        itemsTransitionDuration = 400
    } = props;

    const instanceState = useRef({
        isEnabled: true
    })

    const animatedListItemsMapRef = useRef<Map<React.Key, AnimatedListItemInfo>>()
    const boundingBoxMapRef = useRef<Map<React.Key, IArea>>();
    const prevBoundingBoxMapRef = useRef<Map<React.Key, IArea>>();
    const removedElementsSet = useInstanceValue(() => new ObservableSet<ListItemsAnimator.RemovedElementInfo>());

    useObservable(removedElementsSet);
    
    useImperativeHandle(ref, () => {
        return {
            toggleEnabled: (isEnabled) => {
                instanceState.current.isEnabled = isEnabled;
            }
        }
    })

    const childrenKeysArr = React.Children.map(
        children,
        child => assertNotNullable(child.key, `all ListItemsAnimator's children must have a key`)
    );

    const orderedChildrenKeysHash = JSON.stringify(childrenKeysArr);

    useMemo(() => {

        if (disappearTransition && animatedListItemsMapRef.current) {
            const childrenKeysSet = new Set(childrenKeysArr);

            for (const [key, elementInfo] of animatedListItemsMapRef.current) {

                if (!childrenKeysSet.has(key)) {

                    const removedElement = assertNotNullable(elementInfo.ref.current).getDomElement() as HTMLElement;

                    const offsetParent = removedElement.offsetParent as HTMLElement ?? document.body;

                    removedElementsSet.add({
                        key: key,
                        reactElement: elementInfo.element,
                        offsetParent: offsetParent,
                        parentElement: removedElement.parentElement ?? document.body,
                        relativePosition: elementGetClientOuterAreaRelativeToAnotherElement(removedElement, offsetParent),
                        disappearTransition: disappearTransition
                    })
                }
            }
        }
    }, [orderedChildrenKeysHash])


    const mappedChildren = evaluateFunction(() => {

        const updatedAnimatedListItemsMap = new Map<React.Key, AnimatedListItemInfo>();

        const mappedChildren = React.Children.map(children, (child) => {

            const key = assertNotNullable(child.key, `all ListItemsAnimator's children must have a key`);

            const isNewItem = animatedListItemsMapRef.current && !animatedListItemsMapRef.current.has(key);

            const ref = animatedListItemsMapRef.current?.get(key)?.ref ?? React.createRef<DomElementResolver>();

            updatedAnimatedListItemsMap.set(key, {
                ref: ref,
                element: child
            });

            return {
                childElement: (
                    <DomElementResolver
                        ref={ref}
                        key={key}
                    >
                        {evaluateFunction(() => {
                            if (isNewItem && appearTransition) {
                                return (
                                    <DomElementAnimator
                                        autoAnimateOnMount={appearTransition}
                                    >
                                        {child}
                                    </DomElementAnimator>
                                )
                            } else{
                                return child;
                            }
                        })}
                    </DomElementResolver>
                ),
                key: key,
                ref: ref
            }
        });

        animatedListItemsMapRef.current = updatedAnimatedListItemsMap;

        return mappedChildren;
    })

    const renderState = useRenderState({
        mappedChildren: mappedChildren,
        itemsTransitionDuration: itemsTransitionDuration
    })

    // When the children are changed, we'll go over them before they are being rendered
    // and capture their client offset on screen.
    useMemo(() => {
        const boundingBoxMap = prevBoundingBoxMapRef.current = new Map<React.Key, IArea>();

        for (const childInfo of mappedChildren) {

            const domElement = childInfo.ref.current;

            if (domElement) {
                boundingBoxMap.set(
                    childInfo.key,
                    domElement.getDomElement().getBoundingClientRect());
            }
        }
    }, [orderedChildrenKeysHash])

    useLayoutEffect(() => {

        if (!instanceState.current.isEnabled) {
            return;
        }

        const mappedChildren = renderState.current.mappedChildren;

        const boundingBoxMap = boundingBoxMapRef.current = new Map<React.Key, IArea>();

        for (const childInfo of mappedChildren) {
            boundingBoxMap.set(
                childInfo.key,
                assertNotNullable(childInfo.ref.current).getDomElement().getBoundingClientRect());
        }

        for (const childInfo of mappedChildren) {
            const domNode = assertNotNullable(childInfo.ref.current).getDomElement() as HTMLElement;

            const firstBox = prevBoundingBoxMapRef.current?.get(childInfo.key);
            const lastBox = assertDefined(boundingBoxMapRef.current).get(childInfo.key);

            if (firstBox !== undefined && lastBox !== undefined) {
                const changeInX = firstBox.left - lastBox.left;
                const changeInY = firstBox.top - lastBox.top;

                if (changeInX !== 0 || changeInY !== 0) {

                    domNode.animate([
                        {transform: `translate(${changeInX}px,${changeInY}px)`},
                        {transform: `translate(0,0)`},
                    ], {
                        duration: evaluateWhenFunction(renderState.current.itemsTransitionDuration, {changeInX: changeInX, changeInY: changeInY}),
                    })
                }
            }
        }

    }, [orderedChildrenKeysHash, renderState]);

    return (
        <>
            {mappedChildren.map(childInfo => childInfo.childElement)}
            {iterableMapToArray(removedElementsSet, removedElementInfo => {
                return (
                    <AnimatedRemovedItem
                        key={removedElementInfo.key}
                        disappearTransition={removedElementInfo.disappearTransition}
                        parentElement={removedElementInfo.parentElement}
                        elementRelativeArea={removedElementInfo.relativePosition}
                        onExited={() => {
                            removedElementsSet.delete(removedElementInfo)
                        }}
                    >
                        {removedElementInfo.reactElement}
                    </AnimatedRemovedItem>
                )
            })}
        </>
    );
}))

export interface ListItemsAnimator {
    toggleEnabled: (isEnabled: boolean) => void;
}

export namespace ListItemsAnimator {

    export interface RemovedElementInfo {
        key: React.Key;
        reactElement: React.ReactElement;
        offsetParent: HTMLElement;
        parentElement: HTMLElement;
        relativePosition: IArea;
        disappearTransition: DomElementAnimator.EvaluableAnimationSpec;
    }

    export type ItemsTransitionContext = {
        changeInX: number;
        changeInY: number
    }

    export interface Props {
        children: React.ReactElement[];
        appearTransition?: DomElementAnimator.EvaluableAnimationSpec;
        disappearTransition?: DomElementAnimator.EvaluableAnimationSpec;
        itemsTransitionDuration?: Evaluable<(args: ItemsTransitionContext) => (number | undefined)>;
    }

}

const AnimatedRemovedItem = React.memo(function AnimatedRemovedItem (
    props: {
        parentElement: HTMLElement;
        disappearTransition: DomElementAnimator.EvaluableAnimationSpec;
        children: React.ReactElement;
        onExited: () => void;
        elementRelativeArea: IArea;
    }
) {
    const {
        disappearTransition,
        children,
        onExited,
        parentElement,
        elementRelativeArea,
    } = props;

    return ReactDOM.createPortal(
        <DomElementAnimator
            autoAnimateOnMount={(element, context) => {
                const animationSpec = evaluateWhenFunction(disappearTransition, element, context);

                if (animationSpec) {
                    return {
                        ...animationSpec,
                        onBeforeAnimationStart: (domElement) => {

                            domElement.style.position = 'absolute';
                            domElement.style.top = `${elementRelativeArea.top}px`;
                            domElement.style.left = `${elementRelativeArea.left}px`;
                            domElement.style.width = `${elementRelativeArea.width}px`;
                            domElement.style.height = `${elementRelativeArea.height}px`;
                            domElement.style.margin = `0`;
                            domElement.style.boxSizing = `border-box`;

                            animationSpec.onBeforeAnimationStart?.(domElement);
                        }
                    }
                } else {
                    return undefined;
                }
            }}
            onAppeared={onExited}
        >
            {children}
        </DomElementAnimator>,
        parentElement
    )

})